import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"

import SharedTabs from "@/components/shared/SharedTabs"

import { DTA_TAB_IDS, DTA_TAB_LABELS, type DtaTabId } from "./tabLabels"

const Bar = ({ className }: { className: string }) => (
  <div className={`dta-skeleton ${className}`} aria-hidden="true" />
)

const repeat = (count: number, render: (i: number) => React.ReactNode) =>
  Array.from({ length: count }, (_, i) => render(i))

const Field = () => (
  <SpaceBetween size="xxs">
    <Bar className="dta-skeleton-label" />
    <Bar className="dta-skeleton-value" />
  </SpaceBetween>
)

const Rows = ({ count }: { count: number }) => (
  <SpaceBetween size="m">
    {repeat(count, (i) => (
      <Bar key={i} className="dta-skeleton-row" />
    ))}
  </SpaceBetween>
)

const OverviewSkeleton = () => (
  <SpaceBetween size="l">
    <Container header={<Header variant="h2">Workflow</Header>}>
      <div className="dta-skeleton-steps">
        <ColumnLayout columns={4}>
          {repeat(4, (i) => (
            <Bar key={i} className="dta-skeleton-step" />
          ))}
        </ColumnLayout>
      </div>
    </Container>
    <Container header={<Header variant="h2">Agreement Details</Header>}>
      <ColumnLayout columns={2} variant="text-grid">
        {repeat(4, (i) => (
          <Field key={i} />
        ))}
      </ColumnLayout>
    </Container>
  </SpaceBetween>
)

const DetailsSkeleton = () => (
  <Container>
    <div className="dta-skeleton-editor">
      <div className="dta-skeleton-editor-nav">
        {repeat(7, (i) => (
          <Bar key={i} className="dta-skeleton-nav-item" />
        ))}
      </div>
      <SpaceBetween size="l">
        <Bar className="dta-skeleton-section-title" />
        <Container>
          <ColumnLayout columns={2}>
            {repeat(4, (i) => (
              <Field key={i} />
            ))}
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    </div>
  </Container>
)

const CollaborationSkeleton = () => (
  <SpaceBetween size="l">
    <Container header={<Header variant="h2">Access</Header>}>
      <Rows count={3} />
    </Container>
    <ColumnLayout columns={2}>
      <Container header={<Header variant="h2">Discussions</Header>}>
        <Rows count={3} />
      </Container>
      <Container header={<Header variant="h2">Change Proposals</Header>}>
        <Rows count={2} />
      </Container>
    </ColumnLayout>
  </SpaceBetween>
)

const AuditTrailSkeleton = () => (
  <SpaceBetween size="l">
    <Container header={<Header variant="h2">Activity</Header>}>
      <Rows count={3} />
    </Container>
    <Container header={<Header variant="h2">Versions</Header>}>
      <Rows count={2} />
    </Container>
  </SpaceBetween>
)

const TAB_SKELETONS: Record<DtaTabId, React.ReactNode> = {
  overview: <OverviewSkeleton />,
  editor: <DetailsSkeleton />,
  collaboration: <CollaborationSkeleton />,
  "audit-trail": <AuditTrailSkeleton />,
}

/**
 * Loading placeholder for the DTA details page. Renders the real tab strip, so the tab the URL
 * opens is already selected, and a placeholder of that tab's sections in the same header card the
 * loaded page uses.
 */
export default function DataStreamSkeleton() {
  return (
    <header className="dta-header" aria-busy="true" aria-label="Loading DTA">
      <div className="dta-header-main dta-skeleton-header">
        <div>
          <Bar className="dta-skeleton-title" />
          <Bar className="dta-skeleton-meta" />
        </div>
        <div className="dta-header-right">
          <Bar className="dta-skeleton-button" />
          <Bar className="dta-skeleton-button" />
        </div>
      </div>
      <div className="dta-header-tabs">
        <div className="dta-tabs-band">
          <SharedTabs
            ariaLabel="DTA sections"
            actions={<Bar className="dta-skeleton-button" />}
            tabs={DTA_TAB_IDS.map((id) => ({
              id,
              label: DTA_TAB_LABELS[id],
              content: TAB_SKELETONS[id],
            }))}
          />
        </div>
      </div>
    </header>
  )
}
